import { FuelDivider } from '@frete.com/fuel-react';
import { SkeletonList } from 'components/Skeleton/SkeletonList';
import { SkeletonListItem } from 'components/Skeleton/SkeletonList/SkeletonListItem';
import EmptySuggestedList from 'components/States/EmptySuggestedList';
import TruckerListItem from 'components/TruckerListItem';
import { TTruckerItem } from 'context/reducers/truckers.type';
import { useAmplitude } from 'hooks/useAmplitude';
import { useAppContext } from 'hooks/useAppContext';
import { useAppStates } from 'hooks/useAppStates';
import { useLoadTruckers } from 'hooks/useLoadTruckers';
import { usePayPerUseExtraTruckers } from 'hooks/usePayPerUseExtraTruckers';
import { useTracking } from 'hooks/useTracking';
import { useUserCombo } from 'hooks/useUserCombo';
import React, { FC, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useParams } from 'react-router-dom';
import { BannerLockedDrivers } from '../BannerLockedDrivers';
import { BannerPayPerUser } from '../BannerPayPerUse';
import { BannerPlanUpgrade } from '../BannerPlanUpgrade';
import ListHeader from '../ListHeader';
import * as Styled from './styled';

export const SuggestedTruckDrivers: FC<{
  truckerListData: TTruckerItem[];
}> = ({ truckerListData }) => {
  const {
    state: {
      truckers: { meta, radius, isFilterApplied },
      suggestedTruckers: { isFetchingPageSuggested },
      tabs: { activeTab },
      deviceSize: { isDesktop },
    },
  } = useAppContext();

  const { ref: loadingObserverRef, inView: isVisibleLoading } = useInView({ threshold: 0.1 });
  const { ref: hitBottomObserverRef, inView: isVisibleHitBottom } = useInView({ threshold: 0.1 });
  const { hasBannerUpgrade, maxLimitView } = useUserCombo();
  const { hasConsumed, isPayPerUsePlan, hasLockedTruckers, unlockedTruckersAmount } = usePayPerUseExtraTruckers();

  const { sendAmplitude } = useAmplitude();
  const { sendTracking } = useTracking();
  const { freightId } = useParams();
  const { isLoadingPartial, isEmptySuggested, isLoadErrorPartial } = useAppStates();
  const { fetchNextTruckerPage } = useLoadTruckers('lazy');

  const [isFirstTime, setIsFirstTime] = useState(true);
  const [unlockedTruckerList, setUnlockedTruckerList] = useState<TTruckerItem[]>([]);
  const [lockedTruckerList, setLockedTruckerList] = useState<TTruckerItem[]>([]);

  const isBasicPlan =
    meta?.pagination?.reachedEnd === false &&
    meta?.pagination?.total === meta?.pagination?.page &&
    truckerListData?.length >= maxLimitView;

  const isBannerLockedDriversVisible = isBasicPlan && hasBannerUpgrade && !isPayPerUsePlan;

  const isShowBannerPlanUpgradeVisible =
    hasLockedTruckers && truckerListData.length > unlockedTruckersAmount && !isPayPerUsePlan;

  const isShowBannerPayPerUseVisible =
    hasLockedTruckers && truckerListData.length > unlockedTruckersAmount && isPayPerUsePlan && !hasConsumed;

  const isLastPage = meta?.pagination?.page === meta?.pagination?.total;

  const isLoadingRowsVisible =
    !isLastPage && !isFilterApplied && (unlockedTruckerList?.length > 0 || lockedTruckerList?.length > 0);

  const sendAmplitudeEventWhenHitBottom = (): void => {
    if (isFirstTime) {
      sendAmplitude('trucker_listing_full_content_viewed', {
        freightCode: freightId,
        scrollPage: meta?.pagination?.page,
      });
      sendTracking({
        event: {
          name: 'trucker_listing_full_content_viewed',
        },
        data: {
          freightId: `hashed|${freightId}`,
        },
      });

      setIsFirstTime(false);
    }
  };

  useEffect(() => {
    if (hasLockedTruckers && truckerListData.length > meta?.truckersLimitsTotal?.unlocked) {
      if (isPayPerUsePlan && hasConsumed) {
        setUnlockedTruckerList(truckerListData);
        return;
      }

      const lockedTruckers = [...truckerListData];
      const unlockedTruckers = lockedTruckers.splice(0, meta?.truckersLimitsTotal?.unlocked);

      setUnlockedTruckerList(unlockedTruckers);
      setLockedTruckerList(lockedTruckers);

      return;
    }
    setUnlockedTruckerList(truckerListData);
  }, [truckerListData]);

  useEffect(() => {
    if (isVisibleLoading && !isLastPage && !isLoadingPartial && !isFetchingPageSuggested && !isFilterApplied) {
      fetchNextTruckerPage(radius);
    }
  }, [isVisibleLoading, radius]);

  useEffect(() => {
    if (isVisibleHitBottom && isLastPage) {
      sendAmplitudeEventWhenHitBottom();
    }
  }, [isVisibleHitBottom]);

  useEffect(() => {
    if (!isFirstTime) setIsFirstTime(true);
  }, [activeTab]);

  return (
    <>
      {isLoadingPartial ? (
        <SkeletonList selfPadding />
      ) : (
        <>
          {!isEmptySuggested && !isLoadErrorPartial && (
            <>
              {isDesktop && <ListHeader />}

              <Styled.List data-testid="suggested-trucker-list">
                {unlockedTruckerList?.map((item, index) => (
                  <TruckerListItem
                    truckerItem={item}
                    startAsVisualized={index === 0}
                    key={`${item.person.id}-${item.person.name}`}
                    isLocked={false}
                    showDivider={index !== unlockedTruckerList.length - 1}
                  />
                ))}

                {isBannerLockedDriversVisible && <BannerLockedDrivers />}

                {isShowBannerPlanUpgradeVisible && <BannerPlanUpgrade />}

                {isShowBannerPayPerUseVisible && <BannerPayPerUser />}

                {lockedTruckerList?.map((item, index) => (
                  <TruckerListItem
                    truckerItem={item}
                    startAsVisualized={false}
                    key={`${item.person.id}-${item.person.name}`}
                    isLocked
                    showDivider={index !== lockedTruckerList.length - 1}
                  />
                ))}

                {isLoadingRowsVisible && (
                  <div ref={loadingObserverRef}>
                    <FuelDivider size="default" />
                    <SkeletonListItem showDivider={false} />
                  </div>
                )}

                {isLastPage && <div ref={hitBottomObserverRef} />}
              </Styled.List>
            </>
          )}

          {(isEmptySuggested || isLoadErrorPartial) && <EmptySuggestedList />}
        </>
      )}
    </>
  );
};
